import React, { useState } from 'react';
import { TransactionConfirmation, type TransactionFees, type NFTDetails, type TransactionType } from './TransactionConfirmation';
import { type XamanPayloadDetails, type SigningStatus } from './TransactionSigner';
import type { SigningPayload } from '../../types/signing';

/**
 * XamanTransactionModal - Full transaction flow modal with confirmation step
 *
 * ✅ USE THIS FOR: Buy flows and other transactions that need user confirmation before signing
 * - Buying NFTs (shows price breakdown, fees, then proceeds to signing)
 * - Any transaction where you want to show details before the user signs
 *
 * HOW IT WORKS:
 * - Stage 1 (confirmation): Shows transaction details (NFT info, fees, total)
 * - Stage 2 (signing): User signs in Xaman app (uses TransactionConfirmation -> XamanSigningModal)
 * - Stage 3 (success/error): Shows result
 *
 * WHEN TO USE WHICH COMPONENT:
 * - XamanTransactionModal: When you need a confirmation step before signing (buy flows)
 * - XamanSigningModal: When you just need signing without confirmation (sell, cancel, transfer)
 * - XamanModal: ONLY for authentication (login, link wallet) - NEVER for transactions!
 *
 * @example
 * ```tsx
 * <XamanTransactionModal
 *   type="buy"
 *   nft={nftDetails}
 *   fees={transactionFees}
 *   stage={transactionStage}
 *   onStageChange={setTransactionStage}
 *   xamanPayload={payload}
 *   onConfirm={handleConfirm}
 *   onSuccess={handleSuccess}
 *   onError={handleError}
 *   onClose={handleClose}
 * />
 * ```
 *
 * @see XamanSigningModal - For direct transaction signing without confirmation
 * @see XamanModal - For authentication only (login, link wallet)
 */
export type TransactionStage = 'confirmation' | 'signing' | 'success' | 'error';

export interface XamanTransactionModalProps {
  // Transaction details
  type: TransactionType;
  nft: NFTDetails;
  fees: TransactionFees;
  destination?: string;

  // Current stage
  stage: TransactionStage;
  onStageChange: (stage: TransactionStage) => void;

  /** Normalized signing payload (provided after confirmation). Preferred over xamanPayload. */
  payload?: SigningPayload;
  /** @deprecated pass `payload` (use toSigningPayload() to normalize) instead. */
  xamanPayload?: XamanPayloadDetails;

  // Transaction result
  txHash?: string;
  error?: string;

  // Callbacks
  onConfirm: () => Promise<void>; // Called when user confirms transaction
  onSuccess: (txHash: string) => void; // Called when transaction succeeds
  onError: (error: string) => void; // Called on error
  onClose: () => void; // Called to close modal

  // Retry option
  onRetry?: () => void;

  // Styling
  className?: string;

  // Guest purchase (no push notification, QR code only)
  isGuest?: boolean;
}

export const XamanTransactionModal: React.FC<XamanTransactionModalProps> = ({
  type,
  nft,
  fees,
  destination,
  stage,
  onStageChange,
  payload,
  xamanPayload,
  txHash,
  error,
  onConfirm,
  onSuccess,
  onError,
  onClose,
  onRetry,
  className = '',
  isGuest = false,
}) => {
  const [confirmProcessing, setConfirmProcessing] = useState(false);
  const [signingStatus, setSigningStatus] = useState<SigningStatus>('pending');

  const handleConfirm = async () => {
    setConfirmProcessing(true);
    try {
      await onConfirm();
      // Parent should set stage to 'signing' and provide xamanPayload
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to initiate transaction');
      onStageChange('error');
    } finally {
      setConfirmProcessing(false);
    }
  };

  const handleSigningSuccess = (hash: string) => {
    onSuccess(hash);
    // Don't change stage - let XamanSigningModal show its success state
    // The modal closes when user clicks "Done"
  };

  const handleSigningError = (errorMsg: string) => {
    onError(errorMsg);
    onStageChange('error');
  };

  const handleSigningStatusChange = (status: SigningStatus) => {
    setSigningStatus(status);
  };

  // Reset signing status when stage changes back to confirmation
  React.useEffect(() => {
    if (stage === 'confirmation') {
      setSigningStatus('pending');
    }
  }, [stage]);

  return (
    <TransactionConfirmation
      type={type}
      nft={nft}
      fees={fees}
      destination={destination}
      stage={stage}
      payload={payload}
      xamanPayload={xamanPayload}
      onConfirm={handleConfirm}
      onCancel={onClose}
      onSuccess={handleSigningSuccess}
      onRejected={() => onError('Transaction was rejected')}
      onExpired={() => onError('Transaction request expired')}
      processing={confirmProcessing}
      className={className}
      isGuest={isGuest}
    />
  );
};
